import { Column } from 'app/design/primitives/box';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Text } from 'app/design/primitives/text';
import { useFormContext } from 'app/hooks/form';
import type { CreateListingDTO } from 'app/types/listing';
import React, { useRef, useState } from 'react';
import { Pressable, type TextInput } from 'react-native';
import { CategorySection } from 'app/screens/new/components/category-section';
import { ListingSubtype, ListingType, TransactionMode } from 'app/types/listing';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useUser } from 'app/network/user';
import { useTranslation } from 'react-i18next';
import { LoanFeeFormFields, LoanFeeModals, useLoanFeeModals } from 'app/screens/management/loan-fee';
import { isPublicServiceCategory } from 'app/network/category';
import { useUISize } from 'app/utils/web-helpers';
import { TransactionModeSection } from './transaction-mode-section';
import { isDonationListing } from 'app/lib/listing';

function ListingPriceField({
  estimatedPricePrompt,
  serviceCategoryId,
  subtype,
  transactionMode,
  transactionModeFeature,
  type,
}: {
  estimatedPricePrompt: 'enabled' | 'disabled';
  serviceCategoryId?: string | null;
  subtype: CreateListingDTO['subtype'];
  transactionMode: CreateListingDTO['transactionMode'];
  transactionModeFeature: 'enabled' | 'disabled';
  type: CreateListingDTO['type'];
}) {
  const { t } = useTranslation('new');
  const { control } = useFormContext<CreateListingDTO>();
  const isDonation = isDonationListing({ subtype, transactionMode });
  const isExpirable = subtype === ListingSubtype.expirable;
  const isObject = subtype === ListingSubtype.object;
  const isPublicServiceRequest = isPublicServiceCategory(serviceCategoryId);
  const showTransactionPrice =
    transactionModeFeature === 'enabled' &&
    type === ListingType.offer &&
    (isObject || isExpirable) &&
    Boolean(transactionMode);

  if (showTransactionPrice) {
    const isSale = transactionMode === TransactionMode.sell;
    return (
      <ControlledInput
        {...{ control }}
        label={t(isSale ? 'itemInformationSection.priceFieldLabel' : 'itemInformationSection.valueFieldLabel')}
        labelSuffix={isSale ? `(${t('mandatory', { ns: 'common' })})*` : `(${t('optionale', { ns: 'common' })})`}
        name="price"
        nullable={!isSale}
        placeholder={t('itemInformationSection.valueFieldPlaceholder')}
      />
    );
  }

  const showEstimatedPrice =
    estimatedPricePrompt === 'enabled' && type === ListingType.offer && !isPublicServiceRequest && !isDonation;
  if (!showEstimatedPrice) return null;

  return (
    <ControlledInput
      {...{ control }}
      keyboardType="number-pad"
      label={t('itemInformationSection.valueFieldLabel')}
      labelSuffix={`(${t('optionale', { ns: 'common' })})`}
      left={<Text variant="bodyBase">$</Text>}
      name="estimatedPrice"
      nullable
      placeholder={t('itemInformationSection.valueFieldPlaceholder')}
    />
  );
}

export function ItemInformationSection() {
  const { t } = useTranslation('new');
  const { data: user } = useUser();
  const transactionModeEnabled = isFeatureEnabled(FeatureFlag.transactionMode, user?.featureFlags);
  const estimatedPricePromptEnabled = isFeatureEnabled(FeatureFlag.listingPromptEstimatedPrice, user?.featureFlags);

  const { control, setValue, watch } = useFormContext<CreateListingDTO>();
  const refDescription = useRef<TextInput>(null);
  const loanFeeAvailable = isFeatureEnabled(FeatureFlag.loanFee, user?.featureFlags);

  const [loanFeeEnabled, setLoanFeeEnabled] = useState<boolean>(false);
  const loanFeeModalsState = useLoanFeeModals();

  const watchSubtype = watch('subtype');
  const watchType = watch('type');
  const watchTransactionMode = watch('transactionMode');
  const isService = watchSubtype === ListingSubtype.service;
  const isDonation = isDonationListing({ subtype: watchSubtype, transactionMode: watchTransactionMode });

  const description = watch('description');
  const hasDescription = Boolean(description);

  const serviceCategoryId = watch('serviceCategoryId');
  const isSmallUI = useUISize().isSmallUI;

  return (
    <>
      <Column gap="spacing.general.x4">
        {watchType === ListingType.offer ? <TransactionModeSection /> : null}

        <ControlledInput
          {...{ control }}
          label={t(
            isService ? 'itemInformationSection.serviceNameFieldLabel' : 'itemInformationSection.nameFieldLabel',
          )}
          markAsRequired
          name="item.name"
          onSubmitEditing={() => refDescription.current?.focus()}
          placeholder={t(
            isService
              ? 'itemInformationSection.serviceNameFieldPlaceholder'
              : 'itemInformationSection.nameFieldPlaceholder',
          )}
          returnKeyType="next"
          submitBehavior="submit"
          testID="new-listing-title-input"
        />

        <ControlledInput
          {...{ control }}
          innerRef={refDescription}
          label={t('itemInformationSection.descriptionFieldLabel')}
          labelSuffix={watchType === ListingType.offer ? `(${t('suggestede', { ns: 'common' })})` : undefined}
          labelRight={
            hasDescription && watchType === ListingType.offer ? (
              <Pressable
                accessibilityLabel={t('itemInformationSection.clearDescription')}
                accessibilityRole="button"
                hitSlop={8}
                style={{ marginLeft: 'auto' }}
                onPress={() => setValue('description', '', { shouldDirty: true, shouldValidate: true })}
              >
                {({ pressed }) => (
                  <Text
                    color="text.interactive"
                    fontWeight="700"
                    style={{ opacity: pressed ? 0.75 : 1 }}
                    variant="bodySmallMedium"
                  >
                    {t('itemInformationSection.clearDescriptionAction')}
                  </Text>
                )}
              </Pressable>
            ) : null
          }
          markAsRequired={watchType === ListingType.request}
          multiline
          name="description"
          nullable={watchType === ListingType.offer}
          placeholder={t(
            isService
              ? 'itemInformationSection.serviceDescriptionFieldPlaceholder'
              : `itemInformationSection.descriptionFieldPlaceholder.${watchType}`,
          )}
          scrollEnabled={false}
          style={{ minHeight: isSmallUI ? 60 : 100 }}
        />

        <ListingPriceField
          estimatedPricePrompt={estimatedPricePromptEnabled ? 'enabled' : 'disabled'}
          serviceCategoryId={serviceCategoryId}
          subtype={watchSubtype}
          transactionMode={watchTransactionMode}
          transactionModeFeature={transactionModeEnabled ? 'enabled' : 'disabled'}
          type={watchType}
        />

        {loanFeeAvailable && watchType === ListingType.offer && !isDonation ? (
          <LoanFeeFormFields {...{ loanFeeEnabled, setLoanFeeEnabled, modalsState: loanFeeModalsState }} />
        ) : null}

        {isFeatureEnabled(FeatureFlag.popularItemsByCats, user?.featureFlags) ? (
          <CategorySection goBackTo="new" />
        ) : null}
      </Column>
      <LoanFeeModals state={loanFeeModalsState} />
    </>
  );
}
